import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi } from '@/api';
import { Badge, Button, Input, Modal, Select, Textarea, useToast } from '@/components/ui';
import { RECOMMENDATION_LABELS, RECOMMENDATION_TONES } from '@/features/interviews/status';
import { errorMessage } from '@/hooks/useApiError';

/**
 * Closes a scheduled interview, or adds feedback to one already completed.
 * Both end in the same place, so they share a dialog.
 */
export function CloseDialog({ interview, onClose }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const alreadyClosed = interview.status === 'COMPLETED';
  const [attended, setAttended] = useState(true);
  // List rows carry only the count, so the panel's actual verdicts are read
  // here rather than threaded through the list payload.
  const { data: detail } = useQuery({
    queryKey: ['company-interview', interview.id],
    queryFn: () => companyApi.getInterview(interview.id),
  });
  const [rating, setRating] = useState(4);
  const [recommendation, setRecommendation] = useState('YES');
  const [comment, setComment] = useState('');

  const submit = useMutation({
    mutationFn: () => {
      const feedback = { rating: Number(rating), recommendation, comment: comment || null };
      if (alreadyClosed) return companyApi.recordInterviewFeedback(interview.id, feedback);
      return companyApi.closeInterview(interview.id, {
        outcome: attended ? 'COMPLETED' : 'NO_SHOW',
        // An interview nobody attended cannot carry feedback.
        feedback: attended ? feedback : null,
      });
    },
    onSuccess: () => {
      toast.success(
        alreadyClosed ? 'Feedback saved' : 'Round closed. The next step is your decision.',
      );
      void queryClient.invalidateQueries({ queryKey: ['company-interviews'] });
      // Closing a round moves the application to "decision pending".
      void queryClient.invalidateQueries({ queryKey: ['company-application'] });
      void queryClient.invalidateQueries({ queryKey: ['company-applications'] });
      void queryClient.invalidateQueries({ queryKey: ['company-application-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['interview-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['company-interview', interview.id] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not save')),
  });

  return (
    <Modal
      open
      title={
        alreadyClosed ? 'Your feedback' : `Record the outcome of round ${interview.round_number}`
      }
      description={
        alreadyClosed
          ? 'Feedback is internal — the candidate never sees it.'
          : 'The application moves to "Decision pending", and you choose the next step: another round, an offer, or a rejection. Feedback stays internal.'
      }
      onClose={onClose}
    >
      <div className="space-y-4">
        {!alreadyClosed && (
          <Select
            label="What happened?"
            value={attended ? 'COMPLETED' : 'NO_SHOW'}
            onChange={(event) => setAttended(event.target.value === 'COMPLETED')}
          >
            <option value="COMPLETED">The interview happened</option>
            <option value="NO_SHOW">The candidate missed the interview</option>
          </Select>
        )}

        {(attended || alreadyClosed) && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Rating"
                type="number"
                min={1}
                max={5}
                hint="1 to 5"
                value={rating}
                onChange={(event) => setRating(event.target.value)}
              />
              <Select
                label="Recommendation"
                value={recommendation}
                onChange={(event) => setRecommendation(event.target.value)}
              >
                {Object.entries(RECOMMENDATION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <Textarea
              label="Notes"
              rows={4}
              placeholder="Solid on SQL, thin on testing. Would pair well with the platform team."
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </>
        )}

        {detail?.feedback?.length > 0 && (
          <div className="border-line border-t pt-3">
            <p className="text-ink-muted mb-2 text-xs font-medium">Already recorded</p>
            <ul className="space-y-2">
              {detail.feedback.map((entry) => (
                <li key={entry.id} className="text-sm">
                  <span className="text-ink font-medium">{entry.rating}/5</span>{' '}
                  <Badge tone={RECOMMENDATION_TONES[entry.recommendation] ?? 'neutral'}>
                    {RECOMMENDATION_LABELS[entry.recommendation]}
                  </Badge>{' '}
                  <span className="text-ink-muted text-xs">
                    — {entry.author_name ?? 'a former teammate'}
                  </span>
                  {entry.comment && (
                    <p className="text-ink-muted mt-0.5 text-xs">{entry.comment}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button isLoading={submit.isPending} onClick={() => submit.mutate()}>
            {alreadyClosed ? 'Save feedback' : 'Save outcome'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
